import React, { useMemo, useState } from 'react';
import { BsPlus } from 'react-icons/bs';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Box, Flex, HStack, Image, Text } from '@chakra-ui/react';

import { CypressIds } from '../../../../../../../../constants/cypressIds';
import { FeatureTypes } from '../../../../../../../../constants/featureTypes';
import emptyBox from '../../../../../../../../empty-box.png';
import { selectCompanyFeatures } from '../../../../../../../../redux/modules/company';
import { showTray } from '../../../../../../../../redux/modules/tray';
import { useCompanyQuestionCategories } from '../../../../../../../../services/company-service';
import { getInfiniteListData } from '../../../../../../../../services/util';
import colors from '../../../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../../../../../types/RequirementTemplateBlueprintType';
import { debounce } from '../../../../../../../../utils/debounce';
import PINSAIAssistantLabel from '../../../../../../../PINSAIAssistantLabel';
import PINSAIOutlineGradientButton from '../../../../../../../PINSAIOutlineGradientButton';
import SearchInputGroup from '../../../../../../../SearchInputGroup';
import LoadingSpinner from '../../../../../../../Spinner';

import CreateCustomQuestionTray from './components/CreateCustomQuestionTray';
import FeatureDisabled from './components/FeatureDisabled';
import QuestionCategoryButton from './components/QuestionCategoryButton';

const EmptyPanel = () => (
  <Flex
    alignItems="center"
    borderTop={`1px solid ${colors.greyScale['200']}`}
    flexDirection="column"
    justifyContent="center"
    px={6}
    py={5}
  >
    <Image height="auto" src={emptyBox} width="160px" />
    <Text color={colors.greyScale['500']} pt="2rem" textStyle="bodyLg500">
      No questions match your search
    </Text>
  </Flex>
);

const TemplateBuilderQuestionPanel = ({ blueprint }) => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const currentTemplate = useSelector(blueprint.selectCurrentRequirementTemplate);
  const companyFeatures = useSelector(selectCompanyFeatures);

  const questionsFeature = useMemo(
    () => companyFeatures.find(feature => feature.name === FeatureTypes.AiTierOne),
    [companyFeatures]
  );

  const customQuestionsEnabled = useMemo(
    () => companyFeatures.find(feature => feature.name === FeatureTypes.AiTierOne)?.isEnabled,
    [companyFeatures]
  );

  const [searchInput, setSearchInput] = useState('');

  const {
    data: questionCategories,
    isLoading,
    isFetching: questionCategoriesIsFetching,
    fetchNextPage,
    hasNextPage
  } = useCompanyQuestionCategories({
    companyId,
    searchTerm: searchInput,
    requirementTemplateId: currentTemplate?.id,
    withDeleted: false
  });

  const questionCategoryData = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return getInfiniteListData(questionCategories);
  }, [isLoading, questionCategories]);

  const currentRequirementTemplateBlocks = useSelector(blueprint.selectCurrentRequirementTemplateBlocks);

  const onSearchTermChange = debounce(value => setSearchInput(value), 700);

  const onSearchChange = ({ target: { value } }) => {
    onSearchTermChange(value);
  };

  if (!questionsFeature?.isEnabled) {
    return (
      <FeatureDisabled companyId={companyId} featureTypeId={questionsFeature?.id} />
    );
  }

  const handleCreateCustomQuestion = () => {
    dispatch(showTray(
      () => (
        <CreateCustomQuestionTray
          blueprint={blueprint}
          companyId={companyId}
          currentRequirementTemplateBlocks={currentRequirementTemplateBlocks}
        />
      ),
      { title: 'Create Custom Question' }
    ));
  };

  return (
    <Flex direction="column" h="100%" justify="flex-start" w="100%">
      <Box mt={4} px={5} py={4}>
        <PINSAIAssistantLabel mb={4} mx="auto" w="200px" />
        <Text color={colors.greyScale['500']} textAlign="left" textStyle="bodySm">
          Select Questions that can be included in your Template and prompt an AI review of what your insured submits.
        </Text>
        <Box w="100%">
          <PINSAIOutlineGradientButton
            data-cy={CypressIds.createCustomQuestionButton}
            isDisabled={!customQuestionsEnabled}
            leftIcon={BsPlus}
            text="Create Custom Question"
            onClick={handleCreateCustomQuestion}
          />
        </Box>
        <HStack justify="space-around" mt={8} spacing={1} width="100%">
          <SearchInputGroup h="40px" placeHolder="Search by Question Name/Language" onChange={onSearchChange} />
        </HStack>
      </Box>

      {!isLoading && questionCategoriesIsFetching && (
        <Box h={2} mb={4}>
          <Text
            color={colors.greyScale['500']}
            textStyle="bodySm500"
          >
            Fetching...
          </Text>
        </Box>
      )}

      {isLoading && (
        <Box h={4}>
          <LoadingSpinner />
        </Box>
      )}

      <Box
        height="auto"
        layerStyle="container"
        marginTop={0}
        maxHeight="100%"
        overflowY="auto"
        position="relative"
        width="100%"
      >
        <InfiniteScroll
          dataLength={questionCategoryData.length}
          hasMore={hasNextPage}
          height={questionCategoryData.length < 10 ? 'auto' : '525px'}
          loader={<Text color={colors.greyScale['600']} textStyle="bodySm">Loading...</Text>}
          next={fetchNextPage}
        >
          {questionCategoryData.length > 0
            ? questionCategoryData.map(questionCategory => (
              <QuestionCategoryButton
                blueprint={blueprint}
                companyId={companyId}
                currentRequirementTemplateBlocks={currentRequirementTemplateBlocks}
                key={questionCategory.id}
                questionCategory={questionCategory}
                searchTerm={searchInput}
              />
            ))
            : (
              <div>
                {!isLoading && <EmptyPanel />}
              </div>
            )}
        </InfiniteScroll>
      </Box>
    </Flex>
  );
};

TemplateBuilderQuestionPanel.propTypes = { blueprint: RequirementTemplateBlueprintType };

export default TemplateBuilderQuestionPanel;
